"use client";

import { useState, useCallback } from "react";

interface MantraJapaCounterProps {
  mantraText: string;
  grahaName: string;
  locale?: "en" | "hi";
  targetCount?: number;
}

export default function MantraJapaCounter({
  mantraText,
  grahaName,
  locale = "en",
  targetCount = 108,
}: MantraJapaCounterProps) {
  const [count, setCount] = useState<number>(0);
  const [rounds, setRounds] = useState<number>(0);
  const isHi = locale === "hi";

  const handleIncrement = useCallback(() => {
    setCount((prev) => {
      const next = prev + 1;
      if (next >= targetCount) {
        setRounds((r) => r + 1);
        return 0;
      }
      return next;
    });
  }, [targetCount]);

  const handleReset = useCallback(() => {
    setCount(0);
    setRounds(0);
  }, []);

  const progressPercent = (count / targetCount) * 100;
  const strokeDashoffset = 283 - (283 * progressPercent) / 100;

  return (
    <div className="rounded-2xl border border-ish-border bg-surface-raised p-6 sm:p-8 my-6 text-center shadow-xs">
      <span className="inline-block rounded-full bg-ish-accent-light px-3 py-1 text-xs font-semibold uppercase tracking-wider text-intent-brand mb-2">
        {isHi ? "दैनिक 108 जाप काउंटर" : "Daily 108 Japa Mala Counter"}
      </span>
      <h3 className="text-xl font-bold font-serif text-ish-dark mb-1">
        {isHi ? `${grahaName} मंत्र साधना` : `${grahaName} Mantra Japa`}
      </h3>
      <p className="text-sm font-serif italic text-intent-brand mb-6 px-4 py-2 bg-ritual-gold/10 rounded-lg border border-ritual-gold/30 max-w-md mx-auto">
        &ldquo;{mantraText}&rdquo;
      </p>

      {/* Interactive Mala Ring */}
      <div className="relative mx-auto flex h-48 w-48 items-center justify-center">
        <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="45"
            className="stroke-border-subtle"
            strokeWidth="6"
            fill="transparent"
          />
          <circle
            cx="50"
            cy="50"
            r="45"
            className="stroke-ish-accent transition-all duration-150 ease-out"
            strokeWidth="6"
            strokeDasharray="283"
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            fill="transparent"
          />
        </svg>

        <button
          type="button"
          onClick={handleIncrement}
          className="group absolute flex h-36 w-36 flex-col items-center justify-center rounded-full bg-white shadow-md hover:shadow-lg transition-transform active:scale-95 border border-border-subtle"
          aria-label={isHi ? "जाप गिनती बढ़ाएं" : "Increment Japa count"}
        >
          <span className="text-3xl font-bold font-mono text-ish-dark group-hover:text-intent-brand transition-colors">
            {count}
          </span>
          <span className="text-eyebrow font-semibold uppercase tracking-wider text-ish-text-muted">
            / {targetCount} {isHi ? "जाप" : "Beads"}
          </span>
          <span className="text-eyebrow text-intent-brand font-medium mt-0.5">
            {isHi ? "टैप करें" : "Tap to Count"}
          </span>
        </button>
      </div>

      {/* Rounds & Controls */}
      <div className="mt-6 flex items-center justify-center gap-6 text-xs text-ish-text-muted">
        <div>
          <span className="font-semibold text-ish-dark">{rounds}</span>{" "}
          {isHi ? "माला पूर्ण" : "Mala Rounds Completed"}
        </div>
        <span>•</span>
        <button
          type="button"
          onClick={handleReset}
          className="text-text-muted hover:text-text-primary underline underline-offset-2 transition"
        >
          {isHi ? "रीसेट करें" : "Reset Counter"}
        </button>
      </div>
    </div>
  );
}
